import emblema from '../assets/emblema.png'
import { juntar } from './ui'

/**
 * A marca da casa: o emblema da FaroEscolar, o nome escrito como no site ("Faro" no azul-noite e "Escolar" no
 * verde da marca) e o selo: o do produto ("CRM", "Marketing" ou "Administração", ADR 0057 e 0059) ou o do
 * sistema inteiro ("System", na tela de entrada, ADR 0072).
 *
 * O nome é texto, não imagem: no modo escuro (ADR 0040) as cores vêm dos tokens e trocam sozinhas, sem uma
 * segunda versão do arquivo. O emblema entra por `import` para o Vite carimbar um hash no nome: quando o
 * arquivo mudar, muda para todo mundo na hora, sem cache velho.
 *
 * O conjunto lê "CRM FaroEscolar" (ou "FaroEscolar Marketing", "FaroEscolar System") em leitor de tela.
 */
const tamanhos = {
  /*
    Pequena (barra do topo): no CRM sem o selo, para caber nos ~140 px que a fila de telas deixa; no Marketing,
    com poucas telas, o selo cabe e diz em que produto a pessoa está.
  */
  p: { emblema: 'h-8', nome: 'text-base', selo: 'text-[9px] px-1.5 py-0.5' },
  m: { emblema: 'h-11', nome: 'text-2xl', selo: 'text-[10px] px-1.5 py-0.5' },
  g: { emblema: 'h-14', nome: 'text-[1.75rem]', selo: 'text-[11px] px-2 py-0.5' },
} as const

export type Produto = 'CRM' | 'Marketing' | 'Administração'

/** O selo diz o produto, ou "System" quando a tela é do sistema todo e não de um produto só. */
type Selo = Produto | 'System'

const corDoSelo: Record<Selo, string> = {
  CRM: 'bg-marca-clara text-marca',
  Marketing: 'bg-faro-verde/15 text-faro-verde',
  Administração: 'bg-faro-ambar/15 text-amber-700 dark:text-faro-ambar',
  /*
    As três cores da faixa da marca, cheias: o sistema é a soma dos produtos, e nenhum deles empresta a cor.
    O texto é a `superficie`: branco sobre o degradê no claro; no escuro o degradê clareia, e o texto escurece.
  */
  System: 'bg-linear-to-r from-marca via-faro-turquesa to-faro-verde text-superficie',
}

export function Marca({
  tamanho = 'p',
  produto = 'CRM',
  comSelo = tamanho !== 'p',
  seloEmbaixo = false,
  className,
}: {
  tamanho?: keyof typeof tamanhos
  produto?: Selo
  comSelo?: boolean
  /**
   * O selo debaixo do nome, e não ao lado. Para caixa estreita: ao lado, "ADMINISTRAÇÃO" quase dobra a largura
   * da marca e saía cortado pela borda do cartão na escolha de produto.
   */
  seloEmbaixo?: boolean
  className?: string
}) {
  const t = tamanhos[tamanho]
  const nome = (
    <span aria-hidden className={juntar('font-titulo font-bold leading-none tracking-tight', t.nome)}>
      <span className="text-tinta-900">Faro</span>
      <span className="text-faro-verde">Escolar</span>
    </span>
  )
  const selo = comSelo ? (
    <span
      aria-hidden
      className={juntar(
        'rounded-md font-titulo font-bold uppercase leading-none tracking-wider',
        corDoSelo[produto],
        t.selo,
      )}
    >
      {produto}
    </span>
  ) : null
  return (
    <span
      role="img"
      aria-label={produto === 'CRM' ? 'CRM FaroEscolar' : `FaroEscolar ${produto}`}
      className={juntar('inline-flex shrink-0 select-none items-center gap-2', className)}>
      <img src={emblema} alt="" className={juntar('w-auto', t.emblema)} draggable={false} />
      {seloEmbaixo && selo ? (
        <span className="flex flex-col items-start gap-1.5">
          {nome}
          {selo}
        </span>
      ) : (
        <>
          {nome}
          {selo}
        </>
      )}
    </span>
  )
}
